import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Container, Row, Col, Button, Alert, Modal, Form, Table, Badge, Spinner, Card } from "react-bootstrap";
import { FaCalendarAlt, FaClock, FaFilePdf, FaTable, FaEdit, FaEye, FaCheck, FaTimes, FaSync, FaPlus, FaInfoCircle } from "react-icons/fa";
import Cookies from "js-cookie";
import axios from "../api/axios";
import { toast } from "react-toastify";

import {
  useRegistrationTypes,
  usePrograms,
  useAcademicYears
} from "../hooks/useDropdownData";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const formatDateForAPI = (date) => {
  if (!date) return "";
  if (date.includes("/")) return date;
  const [y, m, d] = date.split("-");
  return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
};

const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-")) return dateStr;
  const parts = dateStr.split("/");
  if (parts.length === 3) {
    const [d, m, y] = parts;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return dateStr;
};

const ExamTimeTable = () => {
  const userId = Cookies.get("userId") || 1;

  const { registrationTypes } = useRegistrationTypes();
  const { programmes } = usePrograms();
  const { academicYears } = useAcademicYears();

  const [filters, setFilters] = useState({
    academicYear: "",
    examannouncement: "",
    registrationType: "",
    programmes: ""
  });

  const [examAnnouncements, setExamAnnouncements] = useState([]);
  const [timetableData, setTimetableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // add, edit, view
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [statusData, setStatusData] = useState(null);
  
  const [formData, setFormData] = useState({
    exStartDate: "",
    exEndDate: "",
    exStartTime: "",
    exEndTime: ""
  });

  const loadAllExamAnnouncements = useCallback(async () => {
    setLoadingAnnouncements(true);
    try {
      const res = await axios.get(`${BASE_URL}/academic/getAllExamAnnouncement`);
      console.log("All Exam Announcements Response:", res.data);
      
      if (res.data?.status === "1" && res.data?.examannouncement) {
        setExamAnnouncements(res.data.examannouncement);
      } else {
        setExamAnnouncements([]);
      }
    } catch (e) {
      console.error("Load announcements error:", e);
      toast.error("Failed to load exam announcements");
      setExamAnnouncements([]);
    } finally {
      setLoadingAnnouncements(false);
    }
  }, []);

  useEffect(() => {
    loadAllExamAnnouncements();
  }, [loadAllExamAnnouncements]);

  const loadTimetable = useCallback(async () => {
    if (!filters.examannouncement || !filters.registrationType) {
      setTimetableData([]);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/academic/getExamTimeTable`, {
        academicYear: Number(filters.academicYear) || 0,
        examannouncement: Number(filters.examannouncement),
        registrationType: Number(filters.registrationType),
        programmes: Number(filters.programmes) || 0
      });

      console.log("Timetable Response:", res.data);
      
      let data = [];
      if (Array.isArray(res.data)) {
        data = res.data;
      } else if (res.data?.timetable && Array.isArray(res.data.timetable)) {
        data = res.data.timetable;
      } else if (res.data?.data && Array.isArray(res.data.data)) {
        data = res.data.data;
      }
      
      setTimetableData(data);
      
      if (filters.academicYear && data.length > 0) {
        loadStatus();
      }
    } catch (e) {
      console.error("Load timetable error:", e);
      toast.error("Failed to load timetable");
      setTimetableData([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadStatus = async () => {
    if (!filters.academicYear || !filters.examannouncement || !filters.registrationType) {
      return;
    }

    try {
      const res = await axios.post(`${BASE_URL}/master/getTimeTableStatus`, {
        academicyear: Number(filters.academicYear),
        examAnnouncement: Number(filters.examannouncement),
        registrationtype: Number(filters.registrationType)
      });
      console.log("Status Response:", res.data);
      setStatusData(res.data);
    } catch (e) {
      console.error("Failed to load status", e);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadTimetable();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.examannouncement, filters.registrationType, filters.programmes]);

  const handleAdd = () => {
    if (!filters.examannouncement || !filters.registrationType || !filters.programmes) {
      toast.error("Please select Exam Announcement, Registration Type, and Program");
      return;
    }
    
    setModalMode("add");
    setSelectedRecord(null);
    setFormData({
      exStartDate: "",
      exEndDate: "",
      exStartTime: "",
      exEndTime: ""
    });
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setModalMode("edit");
    setSelectedRecord(record);
    setFormData({
      exStartDate: formatDateForInput(record.exStartDate),
      exEndDate: formatDateForInput(record.exEndDate),
      exStartTime: record.exStartTime || "",
      exEndTime: record.exEndTime || ""
    });
    setShowModal(true);
  };

  const handleView = (record) => {
    setModalMode("view");
    setSelectedRecord(record);
    setFormData({
      exStartDate: formatDateForInput(record.exStartDate),
      exEndDate: formatDateForInput(record.exEndDate),
      exStartTime: record.exStartTime || "",
      exEndTime: record.exEndTime || ""
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.exStartDate || !formData.exEndDate || !formData.exStartTime || !formData.exEndTime) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      if (modalMode === "edit" && selectedRecord?.exTimeTableId) {
        const payload = {
          exTimeTableId: selectedRecord.exTimeTableId,
          exStartDate: formatDateForAPI(formData.exStartDate),
          exEndDate: formatDateForAPI(formData.exEndDate),
          exStartTime: formData.exStartTime,
          exEndTime: formData.exEndTime,
          user: Number(userId)
        };
        
        console.log("Update Payload:", payload);
        
        const res = await axios.post(`${BASE_URL}/academic/updateExamTimeTable`, payload);
        
        console.log("Update Response:", res.data);
        
        if (res.data?.status === "1" || res.status === 200 || res.data?.success) {
          toast.success("Time table updated successfully!");
          setShowModal(false);
          loadTimetable();
        } else {
          toast.error(res.data?.message || "Failed to update time table");
        }
      } else {
        const payload = {
          exStartDate: formatDateForAPI(formData.exStartDate),
          exEndDate: formatDateForAPI(formData.exEndDate),
          exStartTime: formData.exStartTime,
          exEndTime: formData.exEndTime,
          exIsActive: 1,
          User: "",
          examannouncement: Number(filters.examannouncement),
          registrationType: Number(filters.registrationType),
          user: Number(userId)
        };
        
        console.log("Create Payload:", payload);
        
        const res = await axios.post(`${BASE_URL}/academic/createExamTimeTable`, payload);
        
        console.log("Create Response:", res.data);
        
        if (res.data?.status === "1" || res.status === 200 || res.data?.success) {
          toast.success("Time table created successfully!");
          setShowModal(false);
          loadTimetable();
        } else {
          toast.error(res.data?.message || "Failed to create time table");
        }
      }
    } catch (e) {
      console.error("Save error:", e);
      toast.error(e.response?.data?.message || "Operation failed");
    }
  };

  const downloadDatePdf = async () => {
    if (!filters.academicYear || !filters.examannouncement || !filters.registrationType) {
      toast.error("Please select Academic Year, Exam Announcement and Registration Type");
      return;
    }

    try {
      const loadingToast = toast.info("Generating PDF...", { autoClose: false });
      
      const payload = {
        academicYear: Number(filters.academicYear),
        examannouncement: Number(filters.examannouncement),
        registrationType: Number(filters.registrationType),
        user: String(userId)
      };
      
      if (filters.programmes) {
        payload.programmes = Number(filters.programmes);
      }
      
      console.log("PDF Request Payload:", payload);
      
      const res = await axios.post(
        `${BASE_URL}/academic/getExamTimeTableOnDatePdf`,
        payload
      );

      toast.dismiss(loadingToast);

      console.log("=== PDF RESPONSE DEBUG ===");
      console.log("Full Response:", res);
      console.log("Response Data:", res.data);
      console.log("Status:", res.data?.status);
      console.log("URL:", res.data?.url);
      console.log("Message:", res.data?.message);
      console.log("========================");

      if (res.data?.status === "1" && res.data?.url) {
        const documentPath = res.data.url;
        
        const baseUrlWithoutApi = BASE_URL.replace('/api', '');
        const baseUrlRoot = window.location.origin;
        
        const pdfUrl1 = `${baseUrlWithoutApi}/documents/${documentPath}`;
        
        const pdfUrl2 = `${baseUrlRoot}/documents/${documentPath}`;
        
        const pdfUrl3 = `${baseUrlRoot}/${documentPath}`;
        
        const pdfUrl4 = `${baseUrlWithoutApi}/${documentPath}`;
        
        console.log("=== PDF URL OPTIONS ===");
        console.log("Option 1 (BASE_URL/documents):", pdfUrl1);
        console.log("Option 2 (origin/documents):", pdfUrl2);
        console.log("Option 3 (origin/path):", pdfUrl3);
        console.log("Option 4 (BASE_URL/path):", pdfUrl4);
        console.log("======================");
        
        const finalPdfUrl = pdfUrl3;
        
        console.log("Opening PDF at:", finalPdfUrl);
        
        const newWindow = window.open(finalPdfUrl, "_blank");
        
        if (!newWindow) {
          const link = document.createElement('a');
          link.href = finalPdfUrl;
          link.download = documentPath.split('/').pop() || 'exam-timetable.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success("PDF download started!");
        } else {
          toast.success(res.data?.message || "PDF generated successfully!");
        }
      } else if (res.data?.status === "2") {
        console.error("Validation Error:", res.data?.message);
        toast.error(res.data?.message || "Failed to generate PDF");
      } else if (res.data?.status === "0") {
        console.error("Server Error:", res.data?.message);
        toast.error(res.data?.message || "Something went wrong while generating PDF");
      } else {
        console.error("Unexpected Response:", res.data);
        toast.error("Unexpected response from server");
      }
    } catch (e) {
      console.error("=== PDF ERROR DEBUG ===");
      console.error("Error:", e);
      console.error("Error Response:", e.response);
      console.error("Error Data:", e.response?.data);
      console.error("Error Status:", e.response?.status);
      console.error("=====================");
      
      toast.dismiss();
      
      if (e.response?.data?.message) {
        toast.error(e.response.data.message);
      } else if (e.response?.data?.status === "2") {
        toast.error(e.response.data.message || "Validation error");
      } else if (e.response?.status === 404) {
        toast.error("PDF endpoint not found. Please check the API URL.");
      } else if (e.response?.status === 500) {
        toast.error("Server error. Please check backend logs.");
      } else {
        toast.error("Failed to generate PDF. Check console for details.");
      }
    }
  };

  const getRegistrationTypeName = (record) => {
    if (record.registrationType) return record.registrationType;
    const type = registrationTypes.find(r => r.id == record.registrationTypeId);
    return type?.exName || type?.name || "";
  };

  const getProgramName = (record) => {
    if (record.programmes) return record.programmes;
    const prog = programmes.find(p => p.id == record.programmesId);
    return prog?.exName || prog?.name || "";
  };

  const getExamAnnouncementName = (id) => {
    const exam = examAnnouncements.find(e => e.id == id);
    return exam?.exName || "";
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-3">
        <Col>
          <Alert variant="info">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>📘 Exam Time Table Management</strong>
                <div className="small mt-2">Create, update & manage exam schedules</div>
              </div>
              {statusData && (
                <Badge bg={statusData.isActive ? "success" : "secondary"}>
                  {statusData.isActive ? "Active" : "Inactive"}
                </Badge>
              )}
            </div>
          </Alert>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Academic Year</Form.Label>
            <Form.Select
              value={filters.academicYear}
              onChange={(e) => setFilters({ ...filters, academicYear: e.target.value })}
            >
              <option value="">All Academic Years</option>
              {academicYears.map(a => (
                <option key={a.id} value={a.id}>{a.exYear || a.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Exam Announcement * {loadingAnnouncements && <Spinner size="sm" animation="border" />}</Form.Label>
            <Form.Select
              value={filters.examannouncement}
              onChange={(e) => setFilters({ ...filters, examannouncement: e.target.value })}
              disabled={loadingAnnouncements}
            >
              <option value="">Select Exam Announcement</option>
              {examAnnouncements.map(e => (
                <option key={e.id} value={e.id}>
                  {e.exName} {e.exIsActive && "✓"} {e.exIsLive && "🔴"}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              {examAnnouncements.length} announcements loaded
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Registration Type *</Form.Label>
            <Form.Select
              value={filters.registrationType}
              onChange={(e) => setFilters({ ...filters, registrationType: e.target.value })}
            >
              <option value="">Select Type</option>
              {registrationTypes.map(r => (
                <option key={r.id} value={r.id}>{r.exName || r.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Program *</Form.Label>
            <Form.Select
              value={filters.programmes}
              onChange={(e) => setFilters({ ...filters, programmes: e.target.value })}
            >
              <option value="">Select Program</option>
              {programmes.map(p => (
                <option key={p.id} value={p.id}>{p.exName || p.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Button 
            variant="primary" 
            onClick={handleAdd}
            disabled={!filters.examannouncement || !filters.registrationType || !filters.programmes}
            className="me-2"
          >
            <FaPlus className="me-1" /> Add New Schedule
          </Button>
          
          <Button 
            variant="outline-danger" 
            onClick={downloadDatePdf}
            disabled={!filters.academicYear || !filters.examannouncement || !filters.registrationType}
            className="me-2"
          >
            <FaFilePdf className="me-1" /> Download PDF
          </Button>

          <Button 
            variant="outline-secondary" 
            onClick={loadTimetable}
            disabled={!filters.examannouncement || !filters.registrationType}
          >
            <FaSync className="me-1" /> Refresh
          </Button>
        </Col>
        <Col className="text-end">
          {timetableData.length > 0 && (
            <small className="text-muted">
              <FaInfoCircle className="me-1" />
              Showing {timetableData.length} record(s)
            </small>
          )}
        </Col>
      </Row>

      <Row>
        <Col>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2">Loading timetable...</div>
            </div>
          ) : timetableData.length > 0 ? (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Course</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Registration Type</th>
                    <th>Program</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timetableData.map((record, idx) => (
                    <tr key={record.exTimeTableId || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        {record.courses && (
                          <>
                            {record.courses}
                            {record.coursesCode && (
                              <small className="text-muted d-block">({record.coursesCode})</small>
                            )}
                          </>
                        )}
                      </td>
                      <td>{record.exStartDate}</td>
                      <td>{record.exEndDate}</td>
                      <td>{record.exStartTime}</td>
                      <td>{record.exEndTime}</td>
                      <td>{getRegistrationTypeName(record)}</td>
                      <td>{getProgramName(record)}</td>
                      <td>
                        {record.exIsActive == 1 || record.exIsActive === true ? (
                          <Badge bg="success">Active</Badge>
                        ) : (
                          <Badge bg="secondary">Inactive</Badge>
                        )}
                      </td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          onClick={() => handleView(record)}
                          className="me-1"
                          title="View"
                        >
                          <FaEye />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline-warning" 
                          onClick={() => handleEdit(record)}
                          title="Edit"
                        >
                          <FaEdit />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="secondary" className="text-center py-5">
              <FaTable size={48} className="mb-3 text-muted" />
              <h5>No Timetable Found</h5>
              <p className="text-muted">
                {!filters.examannouncement || !filters.registrationType
                  ? "Please select Exam Announcement and Registration Type to view timetable"
                  : "No records found for selected filters. Click 'Add New Schedule' to create one."}
              </p>
            </Alert>
          )}
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "add" ? "➕ Add New Schedule" : modalMode === "edit" ? "✏️ Edit Schedule" : "👁️ View Schedule"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMode === "view" && selectedRecord ? (
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <Row className="g-3">
                  {selectedRecord.courses && (
                    <Col md={12}>
                      <div className="p-3 bg-light rounded">
                        <h6 className="text-primary mb-2">📚 Course Details</h6>
                        <p className="mb-1"><strong>Course Name:</strong> {selectedRecord.courses}</p>
                        {selectedRecord.coursesCode && (
                          <p className="mb-0"><strong>Course Code:</strong> {selectedRecord.coursesCode}</p>
                        )}
                      </div>
                    </Col>
                  )}

                  <Col md={6}>
                    <div className="p-3 bg-light rounded">
                      <h6 className="text-success mb-2">📅 Exam Schedule</h6>
                      <p className="mb-1"><strong>Start Date:</strong> {selectedRecord.exStartDate}</p>
                      <p className="mb-1"><strong>End Date:</strong> {selectedRecord.exEndDate}</p>
                      <p className="mb-1"><strong>Start Time:</strong> {selectedRecord.exStartTime}</p>
                      <p className="mb-0"><strong>End Time:</strong> {selectedRecord.exEndTime}</p>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="p-3 bg-light rounded">
                      <h6 className="text-info mb-2">🎓 Academic Info</h6>
                      {selectedRecord.academicYear && (
                        <p className="mb-1"><strong>Academic Year:</strong> {selectedRecord.academicYear}</p>
                      )}
                      {selectedRecord.examAnnouncement && (
                        <p className="mb-1"><strong>Exam Announcement:</strong> {selectedRecord.examAnnouncement}</p>
                      )}
                      <p className="mb-0"><strong>Registration Type:</strong> {getRegistrationTypeName(selectedRecord)}</p>
                    </div>
                  </Col>

                  <Col md={12}>
                    <div className="p-3 bg-light rounded">
                      <h6 className="text-warning mb-2">🏫 Program Details</h6>
                      <p className="mb-0"><strong>Program:</strong> {getProgramName(selectedRecord)}</p>
                    </div>
                  </Col>

                  <Col md={12}>
                    <div className="p-3 bg-light rounded">
                      <h6 className="text-secondary mb-2">📊 Status</h6>
                      <Row>
                        <Col md={4}>
                          <p className="mb-0">
                            <strong>ID:</strong> {selectedRecord.exTimeTableId}
                          </p>
                        </Col>
                        <Col md={4}>
                          <p className="mb-0">
                            <strong>Active Status:</strong>{' '}
                            <Badge bg={selectedRecord.exIsActive ? "success" : "secondary"}>
                              {selectedRecord.exIsActive ? "Active" : "Inactive"}
                            </Badge>
                          </p>
                        </Col>
                        <Col md={4}>
                          <p className="mb-0">
                            <strong>Update Status:</strong>{' '}
                            <Badge bg={selectedRecord.exIsUpdate ? "primary" : "secondary"}>
                              {selectedRecord.exIsUpdate ? "Updated" : "Not Updated"}
                            </Badge>
                          </p>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ) : (
            <>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date * <small className="text-muted">(DD/MM/YYYY)</small></Form.Label>
                    <div className="input-group">
                      <span className="input-group-text"><FaCalendarAlt /></span>
                      <Form.Control
                        type="date"
                        value={formData.exStartDate}
                        onChange={(e) => setFormData({ ...formData, exStartDate: e.target.value })}
                        disabled={modalMode === "view"}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Date * <small className="text-muted">(DD/MM/YYYY)</small></Form.Label>
                    <div className="input-group">
                      <span className="input-group-text"><FaCalendarAlt /></span>
                      <Form.Control
                        type="date"
                        value={formData.exEndDate}
                        onChange={(e) => setFormData({ ...formData, exEndDate: e.target.value })}
                        disabled={modalMode === "view"}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Time * <small className="text-muted">(HH:MM)</small></Form.Label>
                    <div className="input-group">
                      <span className="input-group-text"><FaClock /></span>
                      <Form.Control
                        type="time"
                        value={formData.exStartTime}
                        onChange={(e) => setFormData({ ...formData, exStartTime: e.target.value })}
                        disabled={modalMode === "view"}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Time * <small className="text-muted">(HH:MM)</small></Form.Label>
                    <div className="input-group">
                      <span className="input-group-text"><FaClock /></span>
                      <Form.Control
                        type="time"
                        value={formData.exEndTime}
                        onChange={(e) => setFormData({ ...formData, exEndTime: e.target.value })}
                        disabled={modalMode === "view"}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {selectedRecord && (
                <Alert variant="light" className="mt-3">
                  <Row>
                    <Col md={6}>
                      <small className="text-muted">
                        <strong>📋 Registration Type:</strong> {getRegistrationTypeName(selectedRecord)}<br />
                        <strong>🎓 Program:</strong> {getProgramName(selectedRecord)}<br />
                      </small>
                    </Col>
                    <Col md={6}>
                      <small className="text-muted">
                        {selectedRecord.exTimeTableId && <><strong>🔑 ID:</strong> {selectedRecord.exTimeTableId}<br /></>}
                        <strong>📊 Status:</strong> {selectedRecord.exIsActive == 1 ? "Active" : "Inactive"}
                      </small>
                    </Col>
                  </Row>
                </Alert>
              )}

              {modalMode === "add" && (
                <Alert variant="info" className="mt-3">
                  <small>
                    <strong>ℹ️ Note:</strong> This schedule will be created for:<br />
                    • Exam: <strong>{getExamAnnouncementName(filters.examannouncement)}</strong><br />
                    • Registration Type: <strong>{registrationTypes.find(r => r.id == filters.registrationType)?.exName}</strong><br />
                    • Program: <strong>{programmes.find(p => p.id == filters.programmes)?.exName}</strong>
                  </small>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            <FaTimes className="me-1" /> Close
          </Button>
          {modalMode !== "view" && (
            <Button variant="primary" onClick={handleSave}>
              <FaCheck className="me-1" /> {modalMode === "edit" ? "Update" : "Save"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ExamTimeTable;